import { ReactElement, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { queryKeysClient } from 'common/clients/query-key.client';
import { queryClient } from 'common/clients/query.client';
import { Button } from 'common/components/antd/Button/Button';
import { Form } from 'common/components/antd/Form/Form';
import { Input } from 'common/components/antd/Input/Input';
import { Space } from 'common/components/antd/Space/Space';
import { Body, Footer, Header, EnhancedModal } from 'common/components/EnhancedModal/EnhancedModal';
import { EnhancedSelect } from 'common/components/EnhancedSelect/EnhancedSelect';
import { ENV_COUNTERPARTY_GUID } from 'common/constants/env.constants';
import { EQueryKeys } from 'common/enums/query-kyes.enum';
import { notifySuccess } from 'common/utils/notifier.utils';
import { TCounterpartiesListResData, TCounterpartyEntity } from 'domains/counterparties/types/counterparty.types';
import { useCreateRoleMutation } from 'domains/roles/queries/roles.queries';
import {
  SFooter,
} from './modal.styles';
import { EFormDataNames } from '../enums/roles.enum';
import { TCreateRoleModalData } from '../types/roles.types';

type TProps = {
  isOpen: boolean;
  onCancelModalHandler: () => void;
  data: TCreateRoleModalData;
};

const CreateRoleModal = (props: TProps): ReactElement => {
  const {
    isOpen,
    onCancelModalHandler,
    data: {
      counterpartiesData,
    },
  } = props;
  const { t } = useTranslation();
  const [isSubmitBtnLoading, setIsSubmitBtnLoading] = useState<boolean>(false);

  const { mutateAsync: createRoleMutation } = useCreateRoleMutation();

  const counterpartyOptions = ((counterpartiesData as TCounterpartiesListResData)?.counterparties || [])
    .map((counterparty: TCounterpartyEntity) => ({
      value: counterparty.guid,
      label: counterparty.name,
    }));

  const submitFormHandler = async (values: Record<EFormDataNames, string>): Promise<void> => {
    try {
      setIsSubmitBtnLoading(true);
      const { counterparty_guid, role_name } = values;

      await createRoleMutation({
        counterpartyGUID: counterparty_guid,
        requestData: { role_name },
      });
      await queryClient.invalidateQueries({
        queryKey: queryKeysClient[EQueryKeys.RbacQueryKeys].getPermissionTableData._def,
      });
      setIsSubmitBtnLoading(false);
        notifySuccess('usrRoleMng.roles.create.notification.success');
      onCancelModalHandler();
    } catch (error) {
      console.error(error);
      setIsSubmitBtnLoading(false);
    }
  };

  return (
    <EnhancedModal open={isOpen} width={480} onCancel={onCancelModalHandler}>
      <Form
        onFinish={submitFormHandler}
        initialValues={{
          [EFormDataNames.CounterpartyGuid]: ENV_COUNTERPARTY_GUID,
        }}
      >
        <Header>{t('usrRoleMng.roles.modal.create.createRole.title')}</Header>
        <Body>
          <Space size={16} direction="vertical">
            <Form.Item
              name={EFormDataNames.RoleName}
              label={t('usrRoleMng.roles.modal.create.name.label')}
            >
              <Input size="large" placeholder={t('usrRoleMng.roles.modal.create.name.placeholder') as string} />
            </Form.Item>
            <Form.Item
              name={EFormDataNames.CounterpartyGuid}
              label={t('usrRoleMng.roles.modal.create.counterparty.title')}
            >
              <EnhancedSelect
                style={{
                  width: '100%',
                }}
                size="large"
                placeholder={t('usrRoleMng.roles.modal.create.counterparty.placeholder')}
                options={counterpartyOptions}
              />
            </Form.Item>
          </Space>
        </Body>
        <Footer>
          <SFooter>
            <Button
              block
              style={{
                marginRight: 12,
              }}
              onClick={onCancelModalHandler}
              size="large"
            >
              {t('usrRoleMng.roles.modal.create.button.cancel')}
            </Button>
            <Button block loading={isSubmitBtnLoading} htmlType="submit" type="primary" size="large">
              {t('usrRoleMng.roles.modal.create.button.create')}
            </Button>
          </SFooter>
        </Footer>
      </Form>
    </EnhancedModal>
  );
};

export default CreateRoleModal;
